/**
 * Validation and transformation utilities for Generate Assessment Plan
 *
 * Pure functions, no side effects — handles dxList normalization
 * and payload construction for the ScribeRequest contract.
 */

import type {
  DxListItem,
  DxListItemOption,
  GenerateAssessmentPlanParams,
  GenerateAssessmentPlanPayload,
} from '@/types/generate-assessment-plan';

/**
 * Normalize a single icd10Options entry
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeIcd10Option = (raw: any): DxListItemOption | null => {
  if (!raw || typeof raw !== 'object') return null;
  const icd10 = String(raw.icd10 ?? '').trim();
  const description = String(raw.description ?? '');
  if (!icd10 && !description) return null;
  return { icd10, description };
};

/**
 * Normalize a single dxList item — coerce types, apply defaults
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeDxListItem = (raw: any): DxListItem => {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid dxList item: expected an object');
  }

  const icd10 = String(raw.icd10 ?? '').trim();
  const dxDescription = String(raw.dxDescription ?? '');

  // Normalize icd10Options array
  let icd10Options: DxListItemOption[] = [];
  if (Array.isArray(raw.icd10Options)) {
    icd10Options = raw.icd10Options
      .map(normalizeIcd10Option)
      .filter((opt): opt is DxListItemOption => opt !== null);
  }

  // Auto-derive icd10Options if empty and we have icd10/description data
  if (icd10Options.length === 0 && (icd10 || dxDescription)) {
    icd10Options = [{ icd10, description: dxDescription }];
  }

  return {
    pageNumber: String(raw.pageNumber ?? ''),
    icd10,
    dxDescription,
    reasoning: String(raw.reasoning ?? ''),
    planNote: String(raw.planNote ?? ''),
    icd10Options,
    fieldName: String(raw.fieldName ?? ''),
    fieldValue: String(raw.fieldValue ?? ''),
    snomedCode: String(raw.snomedCode ?? ''),
    source: String(raw.source ?? ''),
  };
};

/**
 * Validate and normalize the entire dxList array
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeDxList = (raw: any): DxListItem[] => {
  if (!Array.isArray(raw)) {
    throw new Error('dxList must be an array');
  }
  if (raw.length === 0) {
    throw new Error('dxList must contain at least one diagnosis');
  }
  return raw.map((item, index) => {
    try {
      return normalizeDxListItem(item);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      throw new Error(`dxList[${index}]: ${msg}`);
    }
  });
};

/**
 * Validate required URL parameters
 */
export const validateRequiredParams = (
  params: GenerateAssessmentPlanParams,
): { valid: boolean; missingParams: string[] } => {
  const missingParams: string[] = [];
  if (!params.integrationKey) missingParams.push('integrationKey');
  if (!params.partnerName) missingParams.push('partnerName');
  if (!params.patientCode) missingParams.push('patientCode');
  if (!params.patientId) missingParams.push('patientId');
  if (!params.appointmentId) missingParams.push('appointmentId');
  if (!params.locationId) missingParams.push('locationId');
  return { valid: missingParams.length === 0, missingParams };
};

/**
 * Construct the final payload matching the ScribeRequest contract
 */
export const buildGenerateAssessmentPlanPayload = (
  params: GenerateAssessmentPlanParams,
  scribeJobId: string,
  dxList: DxListItem[],
  partnerId: string,
): GenerateAssessmentPlanPayload => {
  return {
    createdBy: partnerId ? `partner:${partnerId}` : 'public-recording',
    scribeJobId,
    sectionTag: 'assessmentAndPlan',
    locationId: params.locationId || null,
    appointmentId: params.appointmentId || null,
    clientPlatform: params.clientPlatform || null,
    workerPlatform: null,
    patientCode: params.patientCode,
    dxList,
  };
};
